import React, { useMemo } from "react";
import cx from "classnames";
import { FormFeedback, FormGroup, Input, Label } from "reactstrap";
import { useTranslate } from "hooks";
import { type FormikProps } from "formik";
import { type InputType } from "reactstrap/types/lib/Input";

import { InfoTooltip } from "components/_Modals/ProjectDetailsModal/InfoTooltip";
import type { FormikFieldType, SelectOption } from "interfaces/shared.types";

type TextInputProps = {
  field: FormikFieldType;
  form: FormikProps<Record<string, string | null | SelectOption>>;
  wrapperClassName: string;
  label: string | React.ReactNode;
  tooltipText: string;
  type: InputType;
  maxLength: number;
  className: string;
};

export const TextInput = ({
  field,
  form,
  wrapperClassName,
  label,
  tooltipText,
  type,
  maxLength,
  className,
  ...restProps
}: TextInputProps) => {
  const meta = form.getFieldMeta(field.name);
  const translate = useTranslate();

  const isInvalid = useMemo(
    () => meta.touched && !!meta.error,
    [meta.error, meta.touched],
  );

  return (
    <FormGroup className={wrapperClassName}>
      {label && (
        <Label
          className="label-small fw-bolder d-flex align-items-center justify-content-between mt-1 text-gray-700"
          for={field.name}
        >
          <div className="d-flex align-items-center">
            {label}
            {tooltipText && <InfoTooltip tooltipText={tooltipText} />}
          </div>
        </Label>
      )}
      {field && (
        <div
          className={cx(
            "d-flex position-relative h-100 border border-gray-300 rounded-2",
            type === "textarea" && "flex-column",
          )}
        >
          <Input
            {...field}
            {...restProps}
            invalid={isInvalid}
            type={type}
            maxLength={maxLength}
            value={field.value || ""}
            style={
              maxLength && type !== "textarea"
                ? { paddingRight: "4.5rem" }
                : undefined
            }
            className={cx(
              className,
              maxLength && "border-0",
              maxLength && type === "textarea" && "pb-4",
            )}
          />
          {!!maxLength && (
            <div
              className={cx(
                "text-nowrap text-gray-400 mb-1 me-2 end-0 bottom-0 position-absolute pe-none",
              )}
            >
              <span className="fw-bolder">{field.value?.length || "0"}</span>
              <span className="text-nowrap"> / {maxLength}</span>
            </div>
          )}
        </div>
      )}
      <FormFeedback className={cx("mt-2", { "d-flex": isInvalid })}>
        {meta?.error && translate(meta.error)}
      </FormFeedback>
    </FormGroup>
  );
};
